import ProtectedComponent from './protected-component.js';

const STORAGE_KEY = 'add-friend-cta-dismissed';

class AddFriendCTA extends ProtectedComponent {
  constructor() {
    super();
    this.isDismissed = false;
    this.friendCount = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.checkDismissed();
    // If user is already set (e.g., on page refresh), check and render
    if (this.currentUser && !this.isDismissed) {
      this.load();
    }
    // Bind event handlers for cleanup
    this.handleFriendUpdate = () => this.loadFriendCount();
    this.handleRouteChange = () => {
      // Re-render when route changes to update close button visibility
      // Just re-render with current friend count - it should already be loaded
      if (this.currentUser && !this.isDismissed) {
        this.render();
        this.attachEventListeners();
      }
    };
    // Listen for friend-related events to update count
    document.addEventListener('friend-request-sent', this.handleFriendUpdate);
    document.addEventListener('friend-request-accepted', this.handleFriendUpdate);
    document.addEventListener('friend-removed', this.handleFriendUpdate);
    // Listen for route changes to update close button visibility
    document.addEventListener('route-changed', this.handleRouteChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.handleFriendUpdate) {
      document.removeEventListener('friend-request-sent', this.handleFriendUpdate);
      document.removeEventListener('friend-request-accepted', this.handleFriendUpdate);
      document.removeEventListener('friend-removed', this.handleFriendUpdate);
    }
    if (this.handleRouteChange) {
      document.removeEventListener('route-changed', this.handleRouteChange);
    }
  }

  checkDismissed() {
    try {
      this.isDismissed = localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (_e) {
      // localStorage might not be available
      this.isDismissed = false;
    }
  }

  getCurrentRoute() {
    // Try multiple methods to get the current route
    // 1. Router's current route (most reliable)
    if (window.router && typeof window.router.getCurrentRoute === 'function') {
      const route = window.router.getCurrentRoute();
      if (route) return route;
    }
    // 2. Location pathname (fallback)
    if (window.location && window.location.pathname) {
      return window.location.pathname;
    }
    // 3. Default to home
    return '/';
  }

  async loadFriendCount() {
    if (!this.currentUser) {
      return;
    }

    try {
      const response = await window.API.request('/friends');
      this.friendCount = response.counts?.friends || 0;
      // Re-render to update close button visibility
      if (!this.isDismissed) {
        this.render();
        this.attachEventListeners();
      }
    } catch (error) {
      window.logger.error('Failed to load friend count', error);
      this.friendCount = 0;
    }
  }

  async load() {
    // Re-check dismissed status in case it changed
    this.checkDismissed();
    if (this.isDismissed) {
      this.innerHTML = '';
      return;
    }

    // Load friend count first
    await this.loadFriendCount();

    this.render();
    this.attachEventListeners();
  }

  clear() {
    this.innerHTML = '';
  }

  dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
      this.isDismissed = true;
      this.clear();
    } catch (_e) {
      // localStorage might not be available
      this.clear();
    }
  }

  render() {
    // Check if we're on the friends page
    const currentRoute = this.getCurrentRoute();
    const isOnFriendsPage = currentRoute === '/friends';

    // Show close button only if:
    // 1. Not on friends page (on main posts screen)
    // 2. User has 2+ friends
    const showCloseButton = !isOnFriendsPage && this.friendCount >= 2;

    this.innerHTML = `
      <section class="add-friend-cta">
        <div class="cta-content">
          <span class="cta-icon">🤝</span>
          <span class="cta-text">Invite a friend to join Fun Old Times!</span>
          <button class="btn-cta" id="inviteFriendBtn" type="button">
            Add Friend
          </button>
        </div>
        ${showCloseButton ? `
          <button class="cta-close" id="closeBtn" type="button" aria-label="Close">
            ×
          </button>
        ` : ''}
      </section>
    `;
  }

  attachEventListeners() {
    const inviteBtn = this.querySelector('#inviteFriendBtn');
    if (inviteBtn) {
      inviteBtn.addEventListener('click', () => {
        this.openInviteModal();
      });
    }

    const closeBtn = this.querySelector('#closeBtn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.dismiss();
      });
    }
  }

  // Open the invite friend modal
  openInviteModal() {
    let inviteModal = document.querySelector('invite-friend-modal');
    if (!inviteModal) {
      inviteModal = document.createElement('invite-friend-modal');
      document.body.appendChild(inviteModal);
    }
    inviteModal.open();
  }
}

customElements.define('add-friend-cta', AddFriendCTA);

export default AddFriendCTA;

